import { createFileRoute, useSearch, Link } from '@tanstack/react-router';
import { Alert, Button, Card, Center, Group, Stack, Text, Title } from '@mantine/core';
import { IconCheck, IconCircleCheck, IconAlertCircle } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { z } from 'zod';
import { trpc } from '../../lib/trpc';

const search = z.object({
  gateway: z.enum(['stripe', 'paypal', 'razorpay', 'paystack']).default('stripe'),
  session_id: z.string().optional(),
});

function PaymentSuccess() {
  const { gateway, session_id: sessionId } = useSearch({ from: '/vendor/payment-success' });
  const verify = trpc.billing.checkout.complete.useMutation();

  // Verification kicks off once on mount; today gateways are stubbed so this
  // surfaces the verbatim "gateway not configured" error message.
  const verified = verify.isSuccess;
  const failed = verify.isError;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Center mih={400}>
        <Card maw={520} w="100%">
          <Stack align="center" gap="md">
            {verify.isPending ? (
              <>
                <IconCircleCheck size={64} stroke={1.2} />
                <Title order={3}>Verifying payment…</Title>
              </>
            ) : verified ? (
              <>
                <IconCheck size={64} color="var(--mantine-color-green-6)" stroke={1.2} />
                <Title order={3}>Payment received</Title>
                <Text c="dimmed" ta="center">
                  Your subscription is now active. Welcome aboard.
                </Text>
              </>
            ) : failed ? (
              <Alert color="red" icon={<IconAlertCircle />}>
                {verify.error?.message ?? 'Verification failed'}
              </Alert>
            ) : sessionId ? (
              <>
                <Text>Click verify to confirm your {gateway} payment.</Text>
                <Button
                  loading={verify.isPending}
                  onClick={() => verify.mutate({ gateway, sessionId })}
                >
                  Verify payment
                </Button>
              </>
            ) : (
              <Alert color="yellow">
                No session id present in the redirect URL — the gateway may not have completed.
              </Alert>
            )}
            <Group>
              <Button component={Link} to="/vendor" variant="default">
                Back to dashboard
              </Button>
              <Button component={Link} to="/vendor/subscription" variant="subtle">
                View subscription
              </Button>
            </Group>
          </Stack>
        </Card>
      </Center>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/payment-success')({
  component: PaymentSuccess,
  validateSearch: search,
});
